import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { initGA, removeGA } from '../utils/analytics';

const CookieConsent = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    functional: false
  });

  const translations = {
    et: {
      title: 'Küpsiste teade',
      description: 'Kasutame küpsiseid teie sirvimiskogemuse parandamiseks ja analüütikaks.',
      moreInfo: 'Rohkem infot',
      settings: 'Seaded',
      accept: 'Nõustun',
      reject: 'Keeldun',
      saveSettings: 'Salvesta seaded',
      settingsTitle: 'Küpsiste seaded',
      settingsDescription: 'Saate valida, milliseid küpsiseid lubada. Vajalikud küpsised on alati aktiivsed.',
      necessary: {
        title: 'Vajalikud küpsised',
        description: 'Need küpsised on vajalikud veebisaidi tööks ega saa neid keelata.',
        required: '(Nõutav)'
      },
      analytics: {
        title: 'Analüütilised küpsised',
        description: 'Aitavad meil mõista, kuidas külastajad saiti kasutavad ja parandada kasutajakogemust.'
      },
      functional: {
        title: 'Funktsionaalsed küpsised',
        description: 'Pakuvad täiustatud funktsioone nagu keele salvestamine ja isikupärastamine.'
      }
    },
    en: {
      title: 'Cookie Notice',
      description: 'We use cookies to improve your browsing experience and for analytics.',
      moreInfo: 'More info',
      settings: 'Settings',
      accept: 'Accept',
      reject: 'Reject',
      saveSettings: 'Save Settings',
      settingsTitle: 'Cookie Settings',
      settingsDescription: 'You can choose which cookies to allow. Necessary cookies are always active.',
      necessary: {
        title: 'Necessary Cookies',
        description: 'These cookies are required for the website to function and cannot be disabled.',
        required: '(Required)'
      },
      analytics: {
        title: 'Analytics Cookies',
        description: 'Help us understand how visitors use the site and improve user experience.'
      },
      functional: {
        title: 'Functional Cookies',
        description: 'Provide enhanced features like language saving and personalization.'
      }
    }
  };

  const t = translations[language] || translations.et;

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Failed to parse cookie preferences', e);
      }
    }
  }, []);

  useEffect(() => {
    // Listen for custom event to open settings
    const handleOpenSettings = () => {
      setIsVisible(true);
      setShowSettings(true);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      functional: true
    };
    setPreferences(allPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(allPreferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Initialize Google Analytics
    initGA();
    window.dispatchEvent(new Event('cookieConsentChanged'));
    
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      functional: false
    };
    setPreferences(minimalPreferences);
    localStorage.setItem('cookieConsent', JSON.stringify(minimalPreferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Remove Google Analytics
    removeGA();
    window.dispatchEvent(new Event('cookieConsentChanged'));
    
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    
    // Initialize or remove GA based on preferences
    if (preferences.analytics) {
      initGA();
    } else {
      removeGA();
    }
    window.dispatchEvent(new Event('cookieConsentChanged'));
    
    setIsVisible(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <div className="container mx-auto max-w-6xl">
          {!showSettings ? (
            // Simple Banner
            <div className="bg-[#0a0a0a] border-2 border-[#D6CE15]/30 rounded-2xl p-6 md:p-8 shadow-2xl">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <Cookie className="text-[#D6CE15] flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      🍪 {t.title}
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                      {t.description}
                    </p>
                    <a 
                      href={language === 'en' ? '/en/kupsiste-poliitika' : '/kupsiste-poliitika'}
                      className="text-[#D6CE15] hover:text-[#A4A71E] text-sm mt-2 inline-block"
                    >
                      {t.moreInfo} →
                    </a>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white border border-[#D6CE15]/20 rounded-lg transition-colors font-medium"
                  >
                    <Settings size={18} />
                    {t.settings}
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-300 border border-gray-600 rounded-lg transition-colors font-medium"
                  >
                    {t.reject}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 bg-[#D6CE15] hover:bg-[#A4A71E] text-black rounded-lg transition-colors font-bold"
                  >
                    {t.accept}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Settings Modal
            <div className="bg-[#0a0a0a] border-2 border-[#D6CE15]/30 rounded-2xl p-6 md:p-8 shadow-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Settings className="text-[#D6CE15]" size={28} />
                  <h3 className="text-2xl font-bold text-white">{t.settingsTitle}</h3>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <p className="text-gray-300 mb-6">{t.settingsDescription}</p>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="bg-[#1a1a1a] border border-[#D6CE15]/20 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-semibold text-white">{t.necessary.title}</h4>
                        <span className="text-xs text-[#D6CE15] font-semibold">{t.necessary.required}</span>
                      </div>
                      <p className="text-sm text-gray-400">{t.necessary.description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-12 h-6 bg-[#D6CE15] rounded-full relative cursor-not-allowed opacity-70">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-black rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-[#1a1a1a] border border-[#D6CE15]/20 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-2">{t.analytics.title}</h4>
                      <p className="text-sm text-gray-400">{t.analytics.description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`w-12 h-6 rounded-full relative transition-colors ${
                          preferences.analytics ? 'bg-[#D6CE15]' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-black rounded-full transition-all ${
                          preferences.analytics ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="bg-[#1a1a1a] border border-[#D6CE15]/20 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-white mb-2">{t.functional.title}</h4>
                      <p className="text-sm text-gray-400">{t.functional.description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`w-12 h-6 rounded-full relative transition-colors ${
                          preferences.functional ? 'bg-[#D6CE15]' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-black rounded-full transition-all ${
                          preferences.functional ? 'right-1' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-6 py-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-300 border border-gray-600 rounded-lg transition-colors font-medium"
                >
                  {t.reject}
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex-1 px-6 py-3 bg-[#D6CE15] hover:bg-[#A4A71E] text-black rounded-lg transition-colors font-bold"
                >
                  {t.saveSettings}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;